/**
 * System Alerts — tenant-scoped log of configuration issues, AI pipeline
 * failures, webhook problems, and delivery issues raised by the backend.
 */
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import StatCard from '@/components/ui/StatCard';
import adminApi, {
  AlertCategory,
  AlertSummary,
  AlertType,
  SystemAlert,
} from '@/services/admin.api';
import { ALERTS_CHANGED_EVENT, markLocalAlertsAction, notifyAlertsChanged } from '@/utils/alerts-events.util';
import {
  CheckCircleOutline,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  NotificationsActive,
  Refresh,
  TaskAlt,
  WarningAmber,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';

const LIMIT = 20;

const typeMeta: Record<AlertType, { label: string; color: string; icon: React.ReactNode }> = {
  critical: { label: 'Critical', color: '#ef4444', icon: <ErrorOutline sx={{ fontSize: 18 }} /> },
  warning: { label: 'Warning', color: '#f59e0b', icon: <WarningAmber sx={{ fontSize: 18 }} /> },
  info: { label: 'Info', color: '#3b82f6', icon: <InfoOutlined sx={{ fontSize: 18 }} /> },
};

/** Must stay exhaustive over AlertCategory — the backend raises `billing`,
 *  `routing` and `audit` alerts too, and a missing entry here renders a blank
 *  category chip on an otherwise perfectly valid alert. `Record<AlertCategory,
 *  string>` is what makes adding a category to the API type a compile error
 *  here rather than a silent gap in the UI. */
const categoryLabel: Record<AlertCategory, string> = {
  configuration: 'Configuration',
  ai_pipeline: 'AI Pipeline',
  webhook: 'Webhook',
  delivery: 'Delivery',
  billing: 'Billing',
  routing: 'Routing',
  audit: 'Audit',
};

// "userMessagePreview" / "waba_id" -> "User Message Preview" / "Waba Id"
const formatContextKey = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const ContextValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  if (typeof value === 'object') {
    return (
      <Box sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', gap: 1, py: 0.25, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
              {formatContextKey(k)}
            </Typography>
            <ContextValue value={v} />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {String(value)}
    </Typography>
  );
};

/** Filters + page in the URL: an alert view is exactly the kind of thing you
 *  paste to a colleague ("here are the unresolved delivery alerts"). */
const ALERTS_URL_SPEC = {
  resolved: field.enumOf(['unresolved', 'resolved'] as const, 'unresolved'),
  type: field.enumOf(['all', 'critical', 'warning', 'info'] as const, 'all'),
  category: field.enumOf(
    // Keep in step with categoryLabel / AlertCategory — a category missing here
    // is silently coerced back to 'all', so its filter link can't be shared.
    ['all', 'configuration', 'ai_pipeline', 'webhook', 'delivery', 'billing', 'routing', 'audit'] as const,
    'all'
  ),
  page: field.pageIndex(),
};

const SystemAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [total, setTotal] = useState(0);
  const { state: query, patch: patchQuery } = useUrlListState(ALERTS_URL_SPEC);
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (value: number) => patchQuery({ page: value });
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const resolvedFilter = query.resolved;
  const setResolvedFilter = (value: 'unresolved' | 'resolved') => patchQuery({ resolved: value });
  const typeFilter = query.type as 'all' | AlertType;
  const setTypeFilter = (value: 'all' | AlertType) => patchQuery({ type: value });
  const categoryFilter = query.category as 'all' | AlertCategory;
  const setCategoryFilter = (value: 'all' | AlertCategory) => patchQuery({ category: value });

  const actionInFlight = useRef(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params: Parameters<typeof adminApi.getAlerts>[0] = {
        resolved: resolvedFilter === 'resolved',
        page: page + 1,
        limit: LIMIT,
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;

      // Independent reads — fetch in parallel, never sequentially.
      const [alertsRes, summaryRes] = await Promise.all([
        adminApi.getAlerts(params),
        adminApi.getAlertSummary(),
      ]);
      setAlerts(alertsRes.data);
      setTotal(alertsRes.meta?.total ?? 0);
      setSummary(summaryRes);
    } catch (error) {
      console.error('Error fetching system alerts:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [resolvedFilter, typeFilter, categoryFilter, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // A new alert raised (or another session acting) arrives as a socket event that
  // AdminLayout re-dispatches here — refetch quietly (no spinner flash) to stay live.
  useEffect(() => {
    const onChanged = () => {
      if (actionInFlight.current) return; // our own action already refetches/updates
      fetchData(true);
    };
    window.addEventListener(ALERTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ALERTS_CHANGED_EVENT, onChanged);
  }, [fetchData]);

  const resetToFirstPage = () => setPage(0);

  const handleAcknowledge = async (id: string) => {
    setActioningId(id);
    actionInFlight.current = true;
    markLocalAlertsAction();
    try {
      await adminApi.acknowledgeAlert(id);
      setAlerts((prev) =>
        prev.map((a) =>
          a._id === id ? { ...a, acknowledged: true, acknowledgedAt: new Date().toISOString() } : a
        )
      );
      // Acknowledged alerts leave the sidebar badge; the stat cards are unchanged
      // (the alert is still open), so only the badge needs to re-sync.
      notifyAlertsChanged();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      actionInFlight.current = false;
      setActioningId(null);
    }
  };

  const handleResolve = async (id: string) => {
    setActioningId(id);
    actionInFlight.current = true;
    markLocalAlertsAction();
    try {
      await adminApi.resolveAlert(id);
      // The resolved alert leaves the unresolved view — refetch list + summary together.
      await fetchData(true);
      notifyAlertsChanged();
    } catch (error) {
      console.error('Error resolving alert:', error);
    } finally {
      actionInFlight.current = false;
      setActioningId(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="System Alerts"
        subtitle="Configuration issues, AI pipeline failures, and webhook problems detected across your account"
        actions={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={() => fetchData()}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Unresolved"
            value={summary?.total ?? 0}
            icon={<NotificationsActive />}
            color="#4f46e5"
            loading={!summary && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Critical"
            value={summary?.critical ?? 0}
            icon={<ErrorOutline />}
            color="#ef4444"
            loading={!summary && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Warning"
            value={summary?.warning ?? 0}
            icon={<WarningAmber />}
            color="#f59e0b"
            loading={!summary && loading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Info"
            value={summary?.info ?? 0}
            icon={<InfoOutlined />}
            color="#3b82f6"
            loading={!summary && loading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="Status"
                value={resolvedFilter}
                onChange={(e) => {
                  resetToFirstPage();
                  setResolvedFilter(e.target.value as 'unresolved' | 'resolved');
                }}
              >
                <MenuItem value="unresolved">Unresolved</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="Type"
                value={typeFilter}
                onChange={(e) => {
                  resetToFirstPage();
                  setTypeFilter(e.target.value as 'all' | AlertType);
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                label="Category"
                value={categoryFilter}
                onChange={(e) => {
                  resetToFirstPage();
                  setCategoryFilter(e.target.value as 'all' | AlertCategory);
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {(Object.keys(categoryLabel) as AlertCategory[]).map((c) => (
                  <MenuItem key={c} value={c}>
                    {categoryLabel[c]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: '0 !important' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={14} width="65%" />
                  </Box>
                  <Skeleton width={90} height={24} />
                  <Skeleton width={70} height={32} />
                </Box>
              ))}
            </Box>
          ) : alerts.length === 0 ? (
            <EmptyState
              icon={<TaskAlt />}
              title={resolvedFilter === 'unresolved' ? 'No active alerts' : 'No resolved alerts'}
              description={
                resolvedFilter === 'unresolved'
                  ? "You're all caught up — nothing needs attention right now."
                  : 'Resolved alerts will show up here.'
              }
            />
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Alert</TableCell>
                      <TableCell>Occurrences</TableCell>
                      <TableCell>Last Occurred</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alerts.map((alert) => {
                      const meta = typeMeta[alert.type];
                      const isExpanded = expandedId === alert._id;
                      const toggleExpanded = () => setExpandedId(isExpanded ? null : alert._id);
                      const contextEntries = Object.entries(alert.context || {});
                      return (
                        <React.Fragment key={alert._id}>
                          <TableRow
                            hover
                            onClick={toggleExpanded}
                            sx={{
                              cursor: 'pointer',
                              '& > *': { borderBottom: isExpanded ? 'none' : undefined },
                            }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  color: meta.color,
                                }}
                              >
                                {meta.icon}
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ color: meta.color }}
                                >
                                  {meta.label}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={categoryLabel[alert.category]}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 420 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  sx={{ mt: -0.25, ml: -0.5 }}
                                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                >
                                  {isExpanded ? (
                                    <ExpandLess fontSize="small" />
                                  ) : (
                                    <ExpandMore fontSize="small" />
                                  )}
                                </IconButton>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {alert.title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={
                                      isExpanded
                                        ? { display: 'block' }
                                        : {
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }
                                    }
                                  >
                                    {alert.message}
                                  </Typography>
                                  {!alert.acknowledged && resolvedFilter === 'unresolved' && (
                                    <Chip
                                      label="New"
                                      size="small"
                                      sx={{
                                        mt: 0.5,
                                        bgcolor: alpha('#4f46e5', 0.1),
                                        color: '#4f46e5',
                                        height: 18,
                                        fontSize: '0.625rem',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {alert.occurrenceCount}×
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(alert.lastOccurredAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              {resolvedFilter === 'resolved' ? (
                                <Typography variant="caption" color="text.secondary">
                                  Resolved{' '}
                                  {alert.resolvedAt
                                    ? new Date(alert.resolvedAt).toLocaleDateString()
                                    : ''}
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  {!alert.acknowledged && (
                                    <Tooltip title="Acknowledge">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleAcknowledge(alert._id)}
                                          disabled={actioningId === alert._id}
                                          aria-label="Acknowledge alert"
                                        >
                                          {actioningId === alert._id ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <CheckCircleOutline sx={{ fontSize: 18 }} />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Mark resolved">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleResolve(alert._id)}
                                        disabled={actioningId === alert._id}
                                        aria-label="Resolve alert"
                                      >
                                        {actioningId === alert._id ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <TaskAlt sx={{ fontSize: 18 }} />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{ p: 0, ...(isExpanded ? {} : { border: 'none' }) }}
                            >
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
                                  }}
                                >
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={7}>
                                      <Typography variant="overline" color="text.secondary">
                                        Full Message
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                                      >
                                        {alert.message}
                                      </Typography>

                                      <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ mt: 2, display: 'block' }}
                                      >
                                        Trigger
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          gap: 0.75,
                                          flexWrap: 'wrap',
                                          mt: 0.5,
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          label={`Mechanism: ${alert.trigger?.mechanism ?? '—'}`}
                                        />
                                        <Chip
                                          size="small"
                                          label={`Action: ${alert.trigger?.action ?? '—'}`}
                                        />
                                        <Chip
                                          size="small"
                                          label={`Step: ${alert.trigger?.step ?? '—'}`}
                                        />
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                      <Typography variant="overline" color="text.secondary">
                                        Context
                                      </Typography>
                                      {contextEntries.length === 0 ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mt: 0.5 }}
                                        >
                                          No additional context.
                                        </Typography>
                                      ) : (
                                        <Box sx={{ mt: 0.5 }}>
                                          {contextEntries.map(([key, value]) => (
                                            <Box
                                              key={key}
                                              sx={{
                                                display: 'flex',
                                                gap: 1,
                                                py: 0.4,
                                                flexWrap: 'wrap',
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ minWidth: 140 }}
                                              >
                                                {formatContextKey(key)}
                                              </Typography>
                                              <ContextValue value={value} />
                                            </Box>
                                          ))}
                                        </Box>
                                      )}

                                      <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ mt: 2, display: 'block' }}
                                      >
                                        Timeline
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1, py: 0.4 }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ minWidth: 140 }}
                                          >
                                            First Occurred
                                          </Typography>
                                          <Typography variant="body2">
                                            {new Date(alert.firstOccurredAt).toLocaleString()}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, py: 0.4 }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ minWidth: 140 }}
                                          >
                                            Last Occurred
                                          </Typography>
                                          <Typography variant="body2">
                                            {new Date(alert.lastOccurredAt).toLocaleString()}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={LIMIT}
                rowsPerPageOptions={[LIMIT]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemAlerts;
